import React, { useState, useEffect } from 'react';
import { Formik, Form, useField, useFormikContext } from 'formik';
import { FormikControl } from '../formik';
import { OptInConsentMsg } from '../shared';
import { useCloudampDataRef, getCloudampData } from '../../../hooks/useCloudampData';
import {
    INTENT,
    buildOnPageClientInfo,
    writeOnPageHandoff,
    clearOnPageHandoff,
} from '../../../utils/selfScheduleHandoff';
import { ON_PAGE_SELF_SCHEDULE_FORM_ID } from '../../../utils/selfScheduleConfig';
import { onPageSelfScheduleSchema } from './utils/onPageSelfScheduleSchema';
import { isNonEmptyValue, isValidEmailValue, isValidPhoneValue } from './utils/onPageSelfScheduleValidation';
import { onPageSelfScheduleInitialValues } from './utils/onPageSelfScheduleInitialValues';
import { getOnPageFormSubtitle } from './utils/onPagePromoCopy';
import './OnPageSelfScheduleForm.css';

// DOM ids kept identical to corporate's on-page form so GTM triggers written
// once work on both properties.
const SUBMIT_BUTTON_ID = 'corporate-schedule-btn';
const ASK_QUESTION_BUTTON_ID = 'ask-a-question';

// FullStory element names, matched to the self-schedule app's own Step 1
// buttons so FullStory tracks this form and the app's Step 1 as the same CTA
// regardless of which surface the visitor is on.
const SCHEDULE_FS_ELEMENT = 'Self Schedule - Step Next CTA';
const ASK_QUESTION_FS_ELEMENT = 'Self Schedule - Ask Question CTA';

export const defaultOnPageSelfScheduleConfig = {
    title: 'Get Started',
    subtitle: 'Book today and lock in your offer.',
    buttonLabels: {
        schedule: 'See Available Times',
        inquiry: 'Ask A Question',
    },
};

// The validity predicates below come from onPageSelfScheduleValidation.js,
// shared with the Yup schema so the live check and the submit gate can't
// disagree. They are content-only (independent of Formik's touched/error
// state), mirroring the self-schedule app's own Step 1: the checkmark badge
// appears as soon as a field looks right, not only after blur or submit.

// Same copy as the self-schedule app's Step 1 — "Required" when empty, a
// field-specific message when non-empty but malformed. Name fields have no
// malformed state (isValid === isNonEmpty).
const FIELD_VALIDATORS = {
    name: { isValid: isNonEmptyValue },
    email: { isValid: isValidEmailValue, invalidIndicator: 'Enter Valid Email Address' },
    phone: { isValid: isValidPhoneValue, invalidIndicator: 'Enter Valid Phone Number' },
};

// Whether a field is currently showing its error: invalid, and either blurred
// already or the visitor has tried to submit. A submit attempt marks every
// field touched and bumps submitCount, so one click reveals every error at
// once rather than only the field the visitor happened to leave.
const hasVisibleError = (value, kind, touched, submitCount) =>
    !FIELD_VALIDATORS[kind].isValid(value) && (touched || submitCount > 0);

function CheckmarkBadge() {
    return (
        <span className="onpage-valid-badge" aria-hidden="true">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path
                    d="M10.4546 0.196237C10.5852 -0.00381994 10.8536 -0.0601943 11.0538 0.070318C11.2538 0.200889 11.3102 0.469372 11.1797 0.669491L4.40715 11.054C4.34094 11.1555 4.23508 11.2243 4.1156 11.2442C3.99593 11.264 3.87302 11.2328 3.77756 11.158L0.165661 8.32606C-0.0223441 8.17864 -0.0551874 7.90648 0.0921386 7.71844C0.239588 7.53038 0.511705 7.49746 0.699756 7.64491L3.93982 10.1853L10.4546 0.196237Z"
                    fill="white"
                />
            </svg>
        </span>
    );
}

/**
 * Renders a theme-2023 FormikControl input with the self-schedule app's Step-1
 * visuals layered on top: an external bold, compact label with the error
 * indicator appended in parentheses (never a separate line under the input),
 * and a checkmark badge once the field's own content looks valid.
 *
 * FormikControl's own label and its below-input ErrorMessage are suppressed —
 * no `label` prop is passed, and `.text-error` is hidden via CSS — since this
 * component owns both. See OnPageSelfScheduleForm.css.
 */
function ValidatedField({ name, label, control, type, kind, placeholder }) {
    const [field, meta] = useField(name);
    const { submitCount } = useFormikContext();
    const { isValid: isValidValue, invalidIndicator } = FIELD_VALIDATORS[kind];

    const isValid = isValidValue(field.value);
    const showError = hasVisibleError(field.value, kind, meta.touched, submitCount);
    const errorIndicator = isNonEmptyValue(field.value) && invalidIndicator ? invalidIndicator : 'Required';

    return (
        <div className="onpage-field">
            <label htmlFor={name} className={`onpage-label ${showError ? 'onpage-label-error' : ''}`.trim()}>
                {label}
                {showError && <span className="onpage-label-error-indicator"> ({errorIndicator})</span>}
            </label>
            <div
                className={`position-relative ${isValid ? 'onpage-field-valid' : ''} ${
                    showError ? 'onpage-field-invalid' : ''
                }`.trim()}
            >
                <FormikControl
                    control={control}
                    name={name}
                    {...(type ? { type } : {})}
                    placeholder={placeholder}
                    aria-invalid={showError}
                />
                {isValid && <CheckmarkBadge />}
            </div>
            <div role="status" className="onpage-sr-only">
                {showError ? `${label}: ${errorIndicator}` : ''}
            </div>
        </div>
    );
}

/**
 * On-page self-schedule form (WEBDEV-4187).
 *
 * Collects contact info and hands off to the self-schedule app at its clinic
 * step (or its Ask a Question screen) instead of the app's own Step 1. No lead
 * is created here — the app creates it after reading the handoff.
 *
 * Rendered by Layout in place of <Consult> when useSelfScheduleFlags()
 * reports onPageFormActive.
 */
export function OnPageSelfScheduleForm({ config = defaultOnPageSelfScheduleConfig }) {
    const cloudampDataRef = useCloudampDataRef();

    // Formik resets isSubmitting as soon as the async onSubmit resolves, but
    // writeOnPageHandoff's location.assign() doesn't block — so there is a
    // window where the page is still visible with the buttons re-enabled, wide
    // enough for a second click to fire another handoff write before the
    // browser navigates. isRedirecting latches the buttons disabled until that
    // navigation actually happens — and the pageshow handler below releases it
    // if the visitor comes back.
    const [isRedirecting, setIsRedirecting] = useState(false);

    // Drop any payload a previous visit left behind. The app's reader deletes
    // the key on read, but that is a guarantee in another repo — without this,
    // a visitor who pressed Back before the app consumed it would leave a full
    // contact record in sessionStorage for the life of the tab.
    //
    // Back from /schedule usually restores this page from the back/forward
    // cache without re-mounting it, so neither the cleanup above nor the
    // isRedirecting reset would run, and both buttons stay stuck on
    // "Submitting...". `pageshow` with `persisted` marks that restore, so both
    // happen there too.
    useEffect(() => {
        clearOnPageHandoff();

        const handlePageShow = (event) => {
            if (!event.persisted) return;
            setIsRedirecting(false);
            clearOnPageHandoff();
        };

        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    const handleSubmit = async (values) => {
        // Captured before the handoff write so the app's self_schedule_timing
        // span covers the visitor's whole wait. Gated on schedule intent only,
        // matching the consult form's own 'Self Schedule' gate. Falls back to
        // 'schedule' the same way writeOnPageHandoff does, so an Enter-key
        // submit with no button onClick is still timed correctly.
        const effectiveIntent = values.intent || INTENT.schedule;
        const startedAt = effectiveIntent === INTENT.schedule ? Date.now() : undefined;

        const clientInfo = buildOnPageClientInfo(values);

        // Set before writeOnPageHandoff, not after: it navigates away from here
        // on whichever path it takes — its normal one or its storage-failure
        // fallback — so isRedirecting should latch either way.
        setIsRedirecting(true);
        writeOnPageHandoff(clientInfo, values.intent, {
            cloudampData: getCloudampData(cloudampDataRef),
            startedAt,
        });
    };

    return (
        <section id={ON_PAGE_SELF_SCHEDULE_FORM_ID} className="onpage-self-schedule-form position-relative">
            {/* Kept for direct #consult deep-links, not for CTA clicks:
                useSelfScheduleCtaRedirect intercepts those and sends them
                straight to /schedule by design — this form is for organic
                scrollers, not CTA traffic. Layout suppresses its own
                <span id="consult"> while this form renders, so the id appears
                exactly once in the document. */}
            <span id="consult" className="anchor-tag"></span>

            <div className="onpage-card">
                <h2 className="onpage-title">{config.title}</h2>
                {/* getOnPageFormSubtitle calls new Date() during render — safe only
                    because this component never renders at build time (gated on the
                    Optimizely variant arriving client-side, see useSelfScheduleFlags
                    in Layout.js). If that ever changes, the server-rendered HTML and
                    the browser's hydration pass would compute this at different
                    times and React would report a mismatch — see onPagePromoCopy.js. */}
                <p className="onpage-subtitle">{getOnPageFormSubtitle(config.subtitle)}</p>

                <Formik
                    initialValues={onPageSelfScheduleInitialValues}
                    validationSchema={onPageSelfScheduleSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={false}
                >
                    {(formik) => {
                        // The buttons stay enabled while fields are invalid, on
                        // purpose. A click is what runs Formik's submit, which marks
                        // every field touched and shows all the errors together;
                        // the schema still stops an invalid submit. Disabling them
                        // on the first visible error meant clicking See Available
                        // Times from an empty, focused field blurred it, disabled
                        // the button before the click landed, and left only that
                        // one field red.
                        //
                        // Busy only once validation has passed: Formik sets
                        // isSubmitting before it validates, so without the
                        // isValidating check an invalid click would flash the
                        // disabled state and "Submitting..." for a frame.
                        const isBusy = isRedirecting || (formik.isSubmitting && !formik.isValidating);

                        return (
                            <Form className="fs-mask onpage-form" method="POST">
                                <input ref={cloudampDataRef} type="hidden" name="cloudamp__data__c" />

                                <div className="d-flex onpage-name-row">
                                    <div className="onpage-name-col">
                                        <ValidatedField
                                            name="first_name"
                                            label="First Name"
                                            control="input"
                                            type="text"
                                            kind="name"
                                            placeholder="First Name"
                                        />
                                    </div>
                                    <div className="onpage-name-col">
                                        <ValidatedField
                                            name="last_name"
                                            label="Last Name"
                                            control="input"
                                            type="text"
                                            kind="name"
                                            placeholder="Last Name"
                                        />
                                    </div>
                                </div>

                                <div className="onpage-field-spacing">
                                    <ValidatedField
                                        name="email"
                                        label="Email Address"
                                        control="emailInput"
                                        kind="email"
                                        placeholder="you@email.com"
                                    />
                                </div>

                                <div className="onpage-field-spacing">
                                    <ValidatedField
                                        name="phone"
                                        label="Phone Number"
                                        control="phoneInput"
                                        type="tel"
                                        kind="phone"
                                        placeholder="+1 999-999-9999"
                                    />
                                </div>

                                {/* No checkbox: clicking either CTA is the marketing
                                    opt-in (WEBDEV-4494), so buildOnPageClientInfo
                                    always hands smsOptIn: true to the app. */}
                                <div className="onpage-legal-text onpage-consent-row">
                                    <OptInConsentMsg
                                        cta={
                                            <>
                                                {config.buttonLabels.inquiry}
                                                <sup>*</sup> or {config.buttonLabels.schedule}
                                                <sup>**</sup>
                                            </>
                                        }
                                    />
                                </div>

                                {/* "See Available Times" is first in the DOM (and the
                                    row is reversed back to its visual order in CSS) so
                                    it is the form's default button — pressing Enter in
                                    any field submits with the schedule intent, not the
                                    question one. */}
                                <div className="onpage-cta-row">
                                    <button
                                        id={SUBMIT_BUTTON_ID}
                                        type="submit"
                                        className="onpage-btn onpage-btn-primary"
                                        disabled={isBusy}
                                        onClick={() => formik.setFieldValue('intent', INTENT.schedule)}
                                        data-fs-element={SCHEDULE_FS_ELEMENT}
                                    >
                                        {isBusy && formik.values.intent === INTENT.schedule ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                {config.buttonLabels.schedule}
                                                <sup>**</sup>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        id={ASK_QUESTION_BUTTON_ID}
                                        type="submit"
                                        className="onpage-btn onpage-btn-secondary"
                                        disabled={isBusy}
                                        onClick={() => formik.setFieldValue('intent', INTENT.question)}
                                        data-fs-element={ASK_QUESTION_FS_ELEMENT}
                                    >
                                        {isBusy && formik.values.intent === INTENT.question ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                {config.buttonLabels.inquiry}
                                                <sup>*</sup>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </section>
    );
}

export default OnPageSelfScheduleForm;
